import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { global } from "@shared/libs/Global";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import SearchPanel from "@widgets/SearchPanel";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { callApi, fnSave } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";
import CommonSpinner from "@widgets/CommonSpinner";

interface popupProps {
  modalRef?: any;
  selectedData?: any;
}

// 프록시 거래처 조회 전용 엔드포인트(저장 없이 목록만 반환).
// 저장까지 하는 기존 배치는 "MASCUST".
const IF_ENDPOINT = "MASCUSTLIST";

// 검색어 한 단어로 OR 매칭할 컬럼. 그리드에 보이는 컬럼만 대상이 된다.
// 프록시 응답은 sys_interface_response 매핑을 거쳐 내부 컬럼명으로 온다(더존 원본 trCd/trNm 아님).
const SEARCH_FIELDS = ["cd_cust", "nm_cust", "nm_cust_sort", "no_biz"];
// 검색창 컨트롤의 cd_param (sys_popupctrl)
const KEYWORD_PARAM = "keyword";

const SN_MAS_BAS_REG_001_POP_003 = forwardRef(
  ({ modalRef, selectedData }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();

    const searchRef = useRef<any>(null);
    const keywordRef = useRef<string>("");
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
    });

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({ grid1: [] });
    const [dataSources, setDataSources] = useState<any>({ grid1: [] });

    const [popupParent, setPopupParent] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    useImperativeHandle(ref, () => ({
      // SnTextBox가 Enter에서 popupRef.current.onSearch()를 호출한다.
      onSearch: applyKeywordFilter,
      checkValidity: async () => {
        return await onSave();
      },
      getOkayData: async () => {
        return {
          closeFlag: true,
          rtnData: [gridRefs.current["grid1"].current?.getCheckedRows() || []],
        };
      },
    }));

    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_MAS_BAS_REG_001_POP_003",
            {}
          );
          setFormControlData(rtn.formControlData);
          setFormData({ ...rtn.formData, ...selectedData });
          setColums({ grid1: rtn.grid.grid1 });
          await fetchCustList();
        } catch (error) {}
      };
      fetchform();
    }, []);

    // 프록시 전체 조회. 팝업 열 때 1회만 부른다.
    const fetchCustList = async () => {
      setLoading(true);
      try {
        const result = await callApi(IF_ENDPOINT, {
          cd_firm: userInfo.cd_firm,
        });
        if (!result?.status) return;

        // 백엔드가 CommonResponse.return_item에 result1 배열을 그대로 담아 준다.
        const rows = Array.isArray(result.data) ? result.data : [];

        setDataSources({ grid1: rows });
        gridRefs.current["grid1"].current?.resetUpdate();
      } catch (error: any) {
      } finally {
        // 성공·실패·중간 return 무엇이든 스피너는 반드시 닫는다.
        setLoading(false);
      }
    };

    // no_biz varchar(10) / no_cust varchar(13) 이라 하이픈을 제거한다.
    const digits = (v: any) => String(v ?? "").replace(/[^0-9]/g, "");

    // 응답이 이미 mas_cust 컬럼명이라 필드 매핑은 하지 않는다. 손대는 건 세 가지뿐:
    //  - cd_firm: 응답값은 더존 회사코드라 로그인 회사코드로 덮는다.
    //  - yn_use: '1'/'0'로 와서 그대로 저장하면 안 되므로 제외한다(insert에서만 'Y'를 넣는다).
    //  - no_biz/no_cust: 자리수 초과를 막기 위해 숫자만 남긴다.
    const toMasCust = ({ yn_use, ...row }: any) => ({
      ...row,
      cd_firm: userInfo.cd_firm,
      no_biz: digits(row.no_biz),
      no_cust: digits(row.no_cust),
    });

    const onSave = async () => {
      try {
        const checkedRows =
          gridRefs.current["grid1"].current?.getCheckedRows() || [];
        if (checkedRows.length === 0) {
          await showDialog("alter", "선택된 거래처가 없습니다.");
          return false;
        }

        // 이미 등록된 거래처코드는 update, 나머지는 insert
        const dup = await callApi("SN_MAS_BAS_REG_001_SAVE_01_dupCdCust", {
          cd_firm: userInfo.cd_firm,
          cdCustString: checkedRows.map((r: any) => r.cd_cust).join(",") + ",",
        });
        // 중복이 0건이면 백엔드가 return_item을 안 채우고, callApi가 그걸 {}로 바꾼다.
        // {}는 truthy라 `|| []`로는 못 걸러지므로 배열인지 직접 본다.
        const existing = new Set(
          (Array.isArray(dup?.data) ? dup.data : []).map((d: any) => d.cd_cust)
        );

        const insert = checkedRows
          .filter((r: any) => !existing.has(r.cd_cust))
          .map((r: any) => ({
            ...toMasCust(r),
            yn_use: "Y",
            cd_user_reg: userInfo.cd_user,
          }));
        // update는 JsonNullable이라 넘긴 컬럼만 덮어쓴다.
        // yn_use는 일부러 제외 — 사용중지된 거래처가 되살아나지 않게.
        const update = checkedRows
          .filter((r: any) => existing.has(r.cd_cust))
          .map(toMasCust);

        const ok = await fnSave("SN_MAS_BAS_REG_001_SAVE_01", {
          mas_cust: {
            modifiedRowcount: insert.length + update.length,
            insert: insert,
            update: update,
            delete: [],
          },
        });
        return !!ok;
      } catch (error: any) {
        return false;
      }
    };

    // 검색어를 4개 컬럼에 OR로 건다. 재조회 없이 그리드 필터만 갱신.
    const applyKeywordFilter = () => {
      gridRefs.current["grid1"].current?.applyLikeFilters(
        { [SEARCH_FIELDS[0]]: keywordRef.current },
        SEARCH_FIELDS
      );
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
        },
      }),
      [columns, dataSources]
    );

    return (
      <div className="h-full flex flex-col">
        <CommonSpinner isLoading={loading} />
        <div className="mb-1">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
            onValueChanged={(event) => {
              if (event.colId === KEYWORD_PARAM) {
                keywordRef.current = String(event.newValue ?? "").trim();
              }
            }}
          ></SearchPanel>
        </div>

        <div className="flex-1 flex flex-col gap-2 min-h-0">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              title: "거래처정보",
              rowSelection: "multiple", // Shift/Ctrl 다중 선택 후 Space로 일괄 체크
              ischeckboxSelection: true, // CHECK 컬럼(fixCheckbox) 생성 → getCheckedRows()
              isEditable: false, // 조회 결과는 수정 대상이 아님
              popupParent: popupParent,
            }}
          />
        </div>
      </div>
    );
  }
);

export default SN_MAS_BAS_REG_001_POP_003;
